import { useForm } from "react-final-form";
import { financialYearLabel } from "./RevenueDetails";
import { useContext, useEffect, useState } from "react";
import { getIn } from "final-form";
import { Modal } from "reactstrap";
import ProvisionalStatement from "./ProvisionalStatement";
import {
  UnderwritingFormContext,
  UnderwritingFormContextType,
} from "../context/UnderwritingFormContext";
import FinancialDocsCard from "./FinancialDocsCard";
import {
  getFyMissingDocsError,
  getFyProvisionalUntilLabel,
  isFyClassificationJobProcessing,
} from "./revenueHelper";

type Props = {
  fieldName: string;
  fieldIndex: number;
  newUploadedDocIds: number[];
  setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  deleteDocIds: number[];
  setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
};

const RevenueRow = ({
  fieldName,
  fieldIndex,
  newUploadedDocIds,
  setNewUploadedDocIds,
  deleteDocIds,
  setDeleteDocIds,
}: Props) => {
  const { getState } = useForm();
  const {
    revenueClassificationJobs,
    clearRevenueClassificationJob,
    clearAllRevenueClassificationData,
  } = useContext(UnderwritingFormContext) as UnderwritingFormContextType;

  const fyYear = getIn(getState()?.values, `${fieldName}.financialYear`);
  const fyClassificationData = revenueClassificationJobs?.[fyYear] ?? null;
  const classificationResult = fyClassificationData?.classificationResult ?? null;
  const classificationErrorDetails = fyClassificationData?.classificationErrorDetails ?? null;

  const isFyClassificationProcessing = isFyClassificationJobProcessing(
    fyClassificationData
  );

  const filesFieldName = `${fieldName}.auditedFinancials`;

  useEffect(() => {
    if (!classificationResult || deleteDocIds.length === 0) return;

    const files: { uploadedFile?: { docId?: number } }[] =
      getIn(getState().values, filesFieldName) ?? [];
    const hasNoUploadedFiles = !files.some((f) => !!f?.uploadedFile?.docId);
    if (hasNoUploadedFiles) {
      clearAllRevenueClassificationData(fyYear);
      return;
    }

    const errorDocIds = classificationResult
      .filter((entry: { docId: number; error: any }) => entry.error && Object.keys(entry.error).length > 0)
      .map((entry: { docId: number; error: any }) => entry.docId);
    if (errorDocIds.length === 0) return;
    const allErrorFilesDeleted = errorDocIds.every((id: number) => deleteDocIds.includes(id));
    if (allErrorFilesDeleted) {
      clearRevenueClassificationJob(fyYear);
    }
  }, [deleteDocIds, classificationResult]);

  const [isOpenProvisional, setIsOpenProvisional] = useState(false);

  const financialYearDetails = getIn(
    getState()?.values,
    `${fieldName}.financialYearDetails`
  );
  const isAuditedFinancials = getIn(
    getState()?.values,
    `${fieldName}.isAuditedFinancials`
  );
  const isFyPassed = financialYearDetails?.fyPassed;
  const isAuditRequired = financialYearDetails?.isAuditedFinancialRequired;

  const badgeText = !isFyPassed
    ? "Provisional"
    : isAuditRequired
    ? "Audited"
    : "Provisional / Audited";

  // The running FY is only asked for provisional numbers, and only up to the
  // last month that has closed — so the card names that cut-off ("Jul 31").
  // Null in the FY's first month, when it has no closed month to name yet.
  const untilLabel = isFyPassed
    ? null
    : getFyProvisionalUntilLabel(financialYearDetails);

  const fyLabel = financialYearLabel(fyYear);

  const cardTitle = `FY ${fyLabel}${
    untilLabel ? ` (Until ${untilLabel})` : ""
  }`;

  const cardDescription = !isFyPassed
    ? `Upload your provisional Balance Sheet${
        untilLabel ? ` up to ${untilLabel}` : ""
      }.`
    : isAuditRequired
    ? "Upload your audited Balance Sheet and P&L."
    : "Upload your Balance Sheet and P&L.";

  return (
    <>
      <FinancialDocsCard
        title={cardTitle}
        badgeText={badgeText}
        description={cardDescription}
        uploadBtnText="Upload Financials"
        filesFieldName={filesFieldName}
        section="financials"
        classificationResult={classificationResult}
        classificationErrorDetails={classificationErrorDetails}
        isProcessing={isFyClassificationProcessing}
        // The running FY (index 0) is not required, so it never reports this.
        missingDocsError={
          isAuditedFinancials && fieldIndex !== 0
            ? getFyMissingDocsError(financialYearDetails, fyLabel)
            : null
        }
        newUploadedDocIds={newUploadedDocIds}
        setNewUploadedDocIds={setNewUploadedDocIds}
        deleteDocIds={deleteDocIds}
        setDeleteDocIds={setDeleteDocIds}
        // Without audited financials the card collects a provisional statement
        // through a modal grid instead of a file picker.
        onUploadClick={
          isAuditedFinancials ? undefined : () => setIsOpenProvisional(true)
        }
      />
      <Modal
        isOpen={isOpenProvisional}
        toggle={() => {
          setIsOpenProvisional(!isOpenProvisional);
        }}
        size="lg"
        fullscreen={true}
        keyboard={false}
      >
        <ProvisionalStatement
          setIsOpen={setIsOpenProvisional}
          fieldName={fieldName}
          financialDetails={financialYearDetails}
          isOpen={isOpenProvisional}
        />
      </Modal>
    </>
  );
};

export default RevenueRow;
